import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import rawCriticalCss from "../critical-css.txt?raw";
import interFontUrl from "../assets/fonts/inter-var.woff2?url";
import oswaldFontUrl from "../assets/fonts/oswald-var.woff2?url";

// The fonts ship as hashed build assets (served `immutable`), so the inlined
// critical CSS has to point at the same hashed URLs as styles.css — otherwise
// the browser downloads each font twice and neither copy is cacheable.
const criticalCss = rawCriticalCss
  .split("/fonts/inter-var.woff2")
  .join(interFontUrl)
  .split("/fonts/oswald-var.woff2")
  .join(oswaldFontUrl);
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteHeader } from "../components/site-header";
import { SiteFooter } from "../components/site-footer";
import { QuoteModalProvider } from "../components/quote-modal";
import { AnalyticsTracker } from "../components/analytics-tracker";
import { OwnerTrust } from "../components/owner-trust";


import { localeFromPath } from "../lib/blog-locales";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Capital Pro Pressure Washing" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Capital Pro Pressure Washing" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Capital Pro Pressure Washing | Exterior Cleaning Albany NY" },
      { property: "og:title", content: "Capital Pro Pressure Washing | Exterior Cleaning Albany NY" },
      { name: "twitter:title", content: "Capital Pro Pressure Washing | Exterior Cleaning Albany NY" },
      { name: "description", content: "Professional exterior cleaning across Albany & New York's Capital Region — house washing, roof washing, gutters, concrete and decks. Veteran owned, 10+ years." },
      { property: "og:description", content: "Professional exterior cleaning across Albany & New York's Capital Region — house washing, roof washing, gutters, concrete and decks. Veteran owned, 10+ years." },
      { name: "twitter:description", content: "Professional exterior cleaning across Albany & New York's Capital Region — house washing, roof washing, gutters, concrete and decks. Veteran owned, 10+ years." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/MeawVaAz9sWjEFQ8Cc5tRHxoNwn1/social-images/social-1785410830522-Untitled_design_(1).webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/MeawVaAz9sWjEFQ8Cc5tRHxoNwn1/social-images/social-1785410830522-Untitled_design_(1).webp" },
    ],
    links: [
      // Tag hosts are dns-prefetch only: the container boots after first paint,
      // so an eager preconnect would just steal bandwidth from the LCP image.
      { rel: "dns-prefetch", href: "https://wcypkvyx.capitalpropressurewashing.com" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://www.google-analytics.com" },
      { rel: "dns-prefetch", href: "https://connect.facebook.net" },
      { rel: "dns-prefetch", href: "https://scripts.clarity.ms" },

      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: interFontUrl,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: oswaldFontUrl,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "style",
        href: appCss,
        "data-defer-css": "",
      },
      // Non-Latin script webfonts (Arabic / Urdu / Chinese) are attached per
      // route in src/lib/blog-locales.ts `scriptFontLinks`, not globally:
      // loading them here blocked rendering on every English page.
    ],


    // The inlined critical CSS is unlayered, which would otherwise beat
    // Tailwind's layered responsive utilities (e.g. `lg:flex`) and force the
    // mobile layout on desktop. Declaring a low-priority layer first keeps it
    // as a paint-fast fallback only.
    styles: [
      {
        children: `@layer critical, theme, base, components, utilities;\n@layer critical {\n${criticalCss}\n}`,
      },
    ],
    scripts: [
      {
        // Long articles (especially translated ones) finish hydrating after a
        // mobile reader has already started scrolling; the router's scroll
        // restoration then fires scrollTo(0,0) and yanks them back to the top.
        // Ignore that single jump-to-top when the reader already scrolled.
        children: `(function(){var o=window.scrollTo.bind(window);var t=Date.now();var done=false;
function isTop(a){if(!a.length)return false;if(typeof a[0]==='object'&&a[0])return !a[0].top&&!a[0].left;return !a[0]&&!a[1]}
window.scrollTo=function(){if(!done&&Date.now()-t<8000&&window.scrollY>120&&isTop(arguments)){done=true;return}return o.apply(window,arguments)};
window.addEventListener('click',function(){done=true},{once:true,passive:true});})();`,
      },
      {
        children: `(function(){function s(){document.querySelectorAll('link[data-defer-css]').forEach(function(l){if(l.rel!=='stylesheet'){l.rel='stylesheet'}})}
function a(){document.querySelectorAll('link[data-defer-css]').forEach(function(l){l.addEventListener('load',function(){l.rel='stylesheet'},{once:true})})}
a();document.addEventListener('DOMContentLoaded',function(){a();requestAnimationFrame(s)});window.addEventListener('load',s);setTimeout(s,2500);})();`,
      },
      {
         // Google Consent Mode v2 — granted by default for all visitors.
         // No banner, nothing visible. Must run BEFORE the GTM loader so all
         // tags see consent signals.
         children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}
gtag('consent','default',{ad_storage:'granted',ad_user_data:'granted',ad_personalization:'granted',analytics_storage:'granted',wait_for_update:0});
gtag('set','url_passthrough',true);
// Google Ads phone-call conversion: tell the AW tag which number to detect
// on the page so it can swap it for a Google forwarding number. Queued into
// the dataLayer — no extra script load, no page-speed cost.
gtag('config','AW-17369477673/pgwfCJfmnNgcEKnktNpA',{phone_conversion_number:'(518) 900-1913'});`,
      },

      {
        // Google Tag Manager — Stape Custom Loader with Cookie Keeper.
        // Official snippet freshly generated from the Stape account API
        // (container wcypkvyx, web GTM-5D8X5LJJ, first-party CDN
        // wcypkvyx.capitalpropressurewashing.com, Cookie Keeper `_cppid`).
        // One deliberate tweak: Cookie Keeper is forced ON for every browser
        // (not Safari-only) and `_cppid` is self-minted when missing, so the
        // `kp` loader path + `bi` identifier are always present. That is what
        // Stape's ITP/Cookie Keeper check looks for.
        // Human-gated load: GTM injects on tap/scroll/key/3 mouse moves, or a 6s
        // visible-tab fallback (skipped for webdriver). Instant on /thank-you and
        // GTM debug URLs. Previously: the GTM script
        // is injected IMMEDIATELY on every page load so even bounce visitors
        // (in-and-out) fire PageView and the ad platforms can learn from them.
        // Consent Mode v2 defaults are set above.
        // NOTE: Do not add additional GTM/GA4 snippets; the container loads here.
        children: `!function(){"use strict";function l(e){for(var t=e,r=0,n=document.cookie.split(";");r<n.length;r++){var o=n[r].split("=");if(o[0].trim()===t)return o[1]}}function s(e){return localStorage.getItem(e)}function u(e){return window[e]}function A(e,t){e=document.querySelector(e);return t?null==e?void 0:e.getAttribute(t):null==e?void 0:e.textContent}var e=window,t=document,r="script",n="dataLayer",o="https://wcypkvyx.capitalpropressurewashing.com",a="",i="afwcypkvyx",c="w=ER1LLCQ0TkI8QS5ePDMpUQ5EREVBXwcZRRoXGxkNAhsICxkbAhwQBA0LExwRCgseFh5YCB8U",g="cookie",v="_cppid",E="",d=!1;try{var d=!0,f="stapeUserId"===g,I=d&&!f?function(e,t,r){void 0===t&&(t="");var n={cookie:l,localStorage:s,jsVariable:u,cssSelector:A},t=Array.isArray(t)?t:[t];if(e&&n[e])for(var o=n[e],a=0,i=t;a<i.length;a++){var c=i[a],c=r?o(c,r):o(c);if(c)return c}else console.warn("invalid uid source",e)}(g,v,E):void 0}catch(e){console.error(e)}I||(I=function(){var e="";try{e=crypto.randomUUID()}catch(t){e=Date.now().toString(36)+Math.random().toString(36).slice(2)}document.cookie="_cppid="+e+";path=/;max-age=63072000;SameSite=Lax"+("https:"===location.protocol?";Secure":"");return e}());var m=e;m[n]=m[n]||[];m[n].push({"gtm.start":(new Date).getTime(),event:"gtm.js"});var v=I?"&bi="+encodeURIComponent(I):"",E=t.createElement(r),f=(d&&(i=8<i.length?i.replace(/([a-z]{8}$)/,"kp$1"):"kp"+i),!d&&a?a:o);E.async=!0,E.src=f+"/"+i+".js?"+c+v;var q=new URLSearchParams(location.search),x="";["gtm_debug","gtm_preview","gtm_auth","gtm_cookies_win"].forEach(function(k){if(q.has(k))x+="&"+k+"="+encodeURIComponent(q.get(k)||"")});var D=0,M=0,T=0,H=["pointerdown","touchstart","keydown","scroll","wheel"],O={capture:!0,passive:!0};function F(){if(D)return;D=1;H.forEach(function(h){removeEventListener(h,F,O)});removeEventListener("mousemove",MM,O);document.removeEventListener("visibilitychange",V);clearTimeout(T);E.src+=x;t.head.appendChild(E)}function MM(){if(++M>=3)F()}function A(){clearTimeout(T);T=setTimeout(function(){document.visibilityState==="visible"?F():0},6e3)}function V(){if(D)return;document.visibilityState==="visible"?A():clearTimeout(T)}if(x||location.pathname.replace(/[/]+$/,"")==="/thank-you"){F()}else{H.forEach(function(h){addEventListener(h,F,O)});addEventListener("mousemove",MM,O);if(!navigator.webdriver){document.addEventListener("visibilitychange",V);if(document.visibilityState==="visible")A()}}}();`,
      },



      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HomeAndConstructionBusiness",
          "@id": "https://capitalpropressurewashing.com/#business",
          name: "Capital Pro Pressure Washing",
          url: "https://capitalpropressurewashing.com",
          telephone: "+15189001913",
          image:
            "https://capitalpropressurewashing.com/__l5e/assets-v1/7fb348af-f23e-48db-88cd-6aeda94adc1a/hero-crew-surface-cleaner.jpg",
          logo: "https://capitalpropressurewashing.com/__l5e/assets-v1/7fb348af-f23e-48db-88cd-6aeda94adc1a/hero-crew-surface-cleaner.jpg",
          priceRange: "$$",
          address: {
            "@type": "PostalAddress",
            streetAddress: "5A Vatrano Rd",
            addressLocality: "Albany",
            addressRegion: "NY",
            postalCode: "12205",
            addressCountry: "US",
          },
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
              ],
              opens: "07:00",
              closes: "19:00",
            },
          ],
          description:
            "Professional exterior cleaning across the Capital Region of New York — house washing, roof washing, gutter cleaning, concrete, decks, and more. 10+ years serving Albany, Schenectady, Saratoga Springs, Troy, Clifton Park, and Rensselaer.",
          areaServed: [
            { "@type": "City", name: "Albany", addressRegion: "NY" },
            { "@type": "City", name: "Schenectady", addressRegion: "NY" },
            { "@type": "City", name: "Clifton Park", addressRegion: "NY" },
            { "@type": "City", name: "Saratoga Springs", addressRegion: "NY" },
            { "@type": "City", name: "Troy", addressRegion: "NY" },
            { "@type": "City", name: "Rensselaer", addressRegion: "NY" },
          ],
          sameAs: [
            "https://www.facebook.com/CapitalProPressureWashing",
            "https://www.instagram.com/capitalpropressurewashing/",
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const locale = localeFromPath(pathname);
  return (
    <html lang={locale.htmlLang} dir={locale.dir}>
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <link rel="stylesheet" href={appCss} />
        </noscript>
        {/* Google Tag Manager (noscript) */}
        <noscript>
          <iframe
            src="https://wcypkvyx.capitalpropressurewashing.com/ns.html?id=GTM-5D8X5LJJ"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {/* End Google Tag Manager (noscript) */}
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isInstantQuote = pathname === "/instant-quote";

  return (
    <QueryClientProvider client={queryClient}>
      <QuoteModalProvider>
        {!isInstantQuote && <SiteHeader />}
        {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
        <Outlet />
        {!isInstantQuote && <OwnerTrust />}
        {!isInstantQuote && <SiteFooter />}
        <AnalyticsTracker />
      </QuoteModalProvider>
    </QueryClientProvider>
  );
}
